import { useField } from 'formik';
import { colors, gradients } from 'theme';
import {
  Box,
  Radio,
  FormLabel,
  RadioGroup,
  FormControl,
  FormHelperText,
  RadioGroupProps,
  FormControlLabel,
} from '@mui/material';

import { controlError } from 'utils';

import { Icon } from '../icons';

export interface RadioFieldProps extends Omit<
  RadioGroupProps,
  'name' | 'onChange'
> {
  name: string;
  label?: string;
  padding?: string;
  required?: boolean;
  fullWidth?: boolean;
  withBorder?: boolean;
  radioGroupClassName?: string;
  options: { label: string; value: string | number }[];
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => void;
}

export const RadioField = ({
  sx,
  name,
  label,
  options,
  padding,
  onChange,
  required,
  fullWidth,
  withBorder,
  radioGroupClassName,
  ...props
}: RadioFieldProps) => {
  const [field, meta, helpers] = useField(name);

  const errorText = controlError(meta, name, label);
  const hasError = Boolean(errorText);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    helpers.setValue(event.target.value);
    onChange?.(event, event.target.value);
  };

  return (
    <FormControl error={hasError} className="w-full">
      {label && (
        <FormLabel className="text-black-primary">
          {label}
          {required && <span className="text-red-primary-primary">*</span>}
        </FormLabel>
      )}
      <RadioGroup
        {...props}
        name={field.name}
        onChange={handleChange}
        value={field.value || ''}
        className={radioGroupClassName}
        sx={{
          ...(fullWidth && { flexWrap: 'nowrap' }),
          ...sx,
        }}
      >
        {options.map((opt) => (
          <Box
            key={opt.value}
            sx={{
              ...(fullWidth && { width: '100%' }),
              ...(withBorder
                ? {
                    padding: '2px',
                    borderRadius: '8px',
                    background:
                      field.value === opt.value
                        ? gradients.accentGradient.primary.primary
                        : colors.gray.primary,
                  }
                : {}),
            }}
          >
            <Box
              sx={
                withBorder
                  ? {
                      borderRadius: '6px',
                      background: colors.white.primary,
                      '&:hover': {
                        background: colors.white.hover,
                      },
                      '&:active': {
                        background: colors.white.active,
                      },
                    }
                  : undefined
              }
            >
              <FormControlLabel
                value={opt.value}
                className="items-start"
                label={<p className="min-h-6 align-middle">{opt.label}</p>}
                sx={
                  padding
                    ? {
                        padding: padding,
                      }
                    : undefined
                }
                control={
                  <Radio
                    icon={<Icon.RadioEmpty />}
                    checkedIcon={<Icon.RadioChecked />}
                  />
                }
              />
            </Box>
          </Box>
        ))}
      </RadioGroup>
      {hasError && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};
